import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Briefcase, FileText, Boxes, Loader2 } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Dedicated full-page universal search. Mirrors the top-bar search but
 * shows full result text, supports pagination by re-running with higher
 * limit, and persists the query in the URL.
 */
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (text) => {
    if (!text.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await v2Api.search(text.trim(), 50);
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err.message);
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get("q")) run(params.get("q"));
    // eslint-disable-next-line
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q });
    run(q);
  };

  const Group = ({ icon: Icon, label, hits, render }) => (
    <Card>
      <CardContent className="p-0">
        <div className="px-4 py-2 border-b bg-slate-50 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Icon className="w-4 h-4" /> {label} <span className="text-slate-400 font-normal">({hits.length})</span>
        </div>
        {hits.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-400 italic">No matches.</p>
        ) : hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => navigate(h.deep_link)}
            className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition"
          >
            {render(h)}
          </button>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Universal Search</h1>
      <p className="text-sm text-slate-500">
        Search across project codes, names, clients, RFP body text (FTS),
        BOQ descriptions and #tags.
      </p>
      <form onSubmit={submit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search…  (try a clause, a tender number, or #priority)"
            className="pl-9"
            autoFocus
          />
        </div>
        <Button type="submit" disabled={loading || !q.trim()}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </Button>
      </form>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      {data && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            {data.hit_count} hits for <em>"{data.query}"</em>
          </p>
          <Group icon={Briefcase} label="Projects" hits={data.projects || []} render={(h) => (
            <>
              <p className="text-sm font-medium text-slate-800">{h.project_code} — {h.project_name}</p>
              {h.snippet && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{h.snippet}</p>}
            </>
          )} />
          <Group icon={FileText} label="RFP excerpts" hits={data.rfp_chunks || []} render={(h) => (
            <>
              <p className="text-xs text-slate-400">
                {h.rfp_id ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      v2Api.rfp.openFileInNewTab(h.rfp_id, h.page).catch(() => {});
                    }}
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    page {h.page} · {h.rfp_title || "RFP"} ↗
                  </a>
                ) : (
                  <>page {h.page} · {h.rfp_title || "RFP"}</>
                )}
                <span className="ml-1">· {h.project_code}</span>
              </p>
              <p className="text-sm text-slate-700 mt-0.5"
                dangerouslySetInnerHTML={{
                  __html: (h.snippet || "")
                    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                    .replace(/&lt;&lt;/g, '<mark class="bg-amber-200">')
                    .replace(/&gt;&gt;/g, "</mark>"),
                }}
              />
            </>
          )} />
          <Group icon={Boxes} label="BOQ lines" hits={data.boq || []} render={(h) => (
            <>
              <p className="text-sm font-medium text-slate-800">{h.title}</p>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{h.snippet}</p>
            </>
          )} />
        </div>
      )}
    </div>
  );
}
